import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable, Injector } from "@angular/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Action, State, StateContext, Store } from "@ngxs/store";
import { createRequestAction, RequestState } from "ngxs-requests-plugin";
import { fetchEventSource } from "@microsoft/fetch-event-source";

import { environment } from "../../../../environments/environment";
import { AppConfigService } from "../../services/app-config.service";
import { LocalStorageService } from "../../services/local-storage.service";

import { ExtractDownload, ExtractDownloadFail, ExtractDownloadStream, ExtractDownloadSuccess, ExtractPreview, ExtractPreviewFail, ExtractPreviewStream, ExtractPreviewSuccess, GetExtractNameFromHash, GetExtractNameFromHashSuccess, GetExtractNameFromHashFail, UpdateExtractName, UpdateExtractNameSuccess, UpdateExtractNameFail, GetAllQueries, GetAllQueriesSuccess, GetAllQueriesFail, GetQueryDetails, GetQueryDetailsSuccess, GetQueryDetailsFail } from "./extract.actions";
import { EMPTY } from "rxjs";
import { IQuery, IQueryReduced } from "../../interfaces/extract.interface";
import { query } from "@angular/animations";
import { ExtractService } from "../../services/extract.service";

export interface ExtractStateModel {
  previewData: any,
  previewIsLoading: boolean,
  downloadIsLoading: boolean,
  loadingMessage: string,
  extractUrl: string,
  extractHash: string,
  extractName: string
}

export interface QueriesStateModel {
  queries: IQueryReduced[]
}

export interface QueryStateModel {
  query: IQuery,
  name: string
}

@RequestState('extractPreview')
@Injectable()
export class ExtractPreviewRequestState {}

@RequestState('extractDownload')
@Injectable()
export class ExtractDownloadRequestState {}

@RequestState('getExtractNameFromHash')
@Injectable()
export class GetExtractNameFromHashRequestState {}

@RequestState('getAllQueries')
@Injectable()
export class GetAllQueriesRequestState {}

@RequestState('getQueryDetails')
@Injectable()
export class GetQueryDetailsRequestState {}

@State<ExtractStateModel>({
  name: 'extract',
  defaults: {
    previewData: null,
    previewIsLoading: false,
    downloadIsLoading: false,
    loadingMessage: null,
    extractUrl: null,
    extractHash: null,
    extractName: null
  }
})
@Injectable()
export class ExtractState {
  private extractStreamAbortController: AbortController | null = null;

  constructor(
    private http: HttpClient,
    private store: Store,
    private localStorageService: LocalStorageService,
    private _snackBar: MatSnackBar,
    private injector: Injector,
    private extractService: ExtractService,
    private appConfig: AppConfigService,
  ) {}

  @Action(ExtractPreview)
  extractPreview(
    { dispatch, patchState }: StateContext<ExtractStateModel>,
  ) {
    const query = this.localStorageService.parseQuery();
    const body = {
      dbName: this.appConfig.dbName,
      ...query,
      preview: true
    };
    
    const request = this.http.post(environment.api + 'db/extract/request', body);

    patchState({previewData: null, loadingMessage: null});

    return dispatch(
      createRequestAction({
        request,
        state: ExtractPreviewRequestState,
        successAction: ExtractPreviewSuccess,
        failAction: ExtractPreviewFail,
      })
    );
  }

  @Action(ExtractPreviewSuccess)
  extractPreviewSuccess(
    { dispatch, patchState }: StateContext<ExtractStateModel>,
    { payload: response }: ExtractPreviewSuccess
  ) {
    if (response.status === 'finished') {
      const {columns, data, displayFormats} = response.content;
      patchState({previewData: {columns, data, displayFormats}});
    } else {
      dispatch(new ExtractPreviewStream(response.hash));
    }
  }

  @Action(ExtractPreviewFail)
  extractPreviewFail(
    { patchState }: StateContext<ExtractStateModel>,
    { payload: error }: ExtractPreviewFail
  ) {
    patchState({loadingMessage: error.error.content});
  }

  @Action(ExtractPreviewStream)
  extractPreviewStream(
    { patchState }: StateContext<ExtractStateModel>,
    { payload: hash }: ExtractPreviewStream
  ) {
    if (this.extractStreamAbortController) {
      this.extractStreamAbortController.abort();
    }
    this.extractStreamAbortController = new AbortController();
    const currentCtrl = this.extractStreamAbortController;

    const token = this.localStorageService.getItem('sequenza_token');
    
    fetchEventSource(environment.api + 'db/extract/stream/' + hash, {
      headers: {
        "Authorization": `Bearer ${token}`
      },
      signal: currentCtrl.signal,
      onmessage: (e) => {
        if (["queued", "shared", "updated", "update"].includes(e.event)) {
          patchState({loadingMessage: e.data});
        } else if (e.event === "finished") {
          const response = JSON.parse(e.data);
          if (!!response) {
            patchState({previewData: response, loadingMessage: null});
            currentCtrl.abort();
          }
        } else if (["invalid", "failed", "error"].includes(e.event)) {
          patchState({loadingMessage: 'Error: ' + e.data});
          currentCtrl.abort();
        }
      },
      onerror: (err) => {
        if (err.name === 'AbortError') {
          return;
        }
        patchState({loadingMessage: 'Error: ' + err});
        currentCtrl.abort();
        throw err;
      }
    });
  }

  @Action(ExtractDownload)
  extractDownload(
    { dispatch, patchState }: StateContext<ExtractStateModel>,
  ) {
    const query = this.localStorageService.parseQuery();
    const body = {
      dbName: this.appConfig.dbName,
      ...query,
      preview: false
    };
    
    const request = this.http.post(environment.api + 'db/extract/request', body);

    patchState({extractUrl: null, loadingMessage: null, extractHash: null, extractName: null});

    return dispatch(
      createRequestAction({
        request,
        state: ExtractDownloadRequestState,
        successAction: ExtractDownloadSuccess,
        failAction: ExtractDownloadFail,
      })
    );
  }

  @Action(ExtractDownloadSuccess)
  extractDownloadSuccess(
    { dispatch, patchState }: StateContext<ExtractStateModel>,
    { payload: response }: ExtractDownloadSuccess
  ) {
    patchState({extractHash: response.hash});
    if (response.name) {
      patchState({extractName: response.name});
    } else {
      dispatch(new GetExtractNameFromHash());
    }
    
    if (response.status === 'finished') {
      const {url} = response.content;
      patchState({extractUrl: url});
    } else {
      dispatch(new ExtractDownloadStream(response.hash));
    }
  }

  @Action(ExtractDownloadFail)
  extractDownloadFail(
    { patchState }: StateContext<ExtractStateModel>,
    { payload: error }: ExtractDownloadFail
  ) {
    patchState({loadingMessage: error.error.content});
  }

  @Action(ExtractDownloadStream)
  extractDownloadStream(
    { patchState }: StateContext<ExtractStateModel>,
    { payload: hash }: ExtractDownloadStream
  ) {
    if (this.extractStreamAbortController) {
      this.extractStreamAbortController.abort();
    }
    this.extractStreamAbortController = new AbortController();
    const currentCtrl = this.extractStreamAbortController;

    const token = this.localStorageService.getItem('sequenza_token');
    
    fetchEventSource(environment.api + 'db/extract/stream/' + hash, {
      headers: {
        "Authorization": `Bearer ${token}`
      },
      signal: currentCtrl.signal,
      onmessage: (e) => {
        if (["queued", "updated", "update"].includes(e.event)) {
          patchState({loadingMessage: e.data});
        } else if (e.event === "finished") {
          const response = JSON.parse(e.data);
          
          if (!!response.url) {
            patchState({loadingMessage: null, extractUrl: response.url});
            currentCtrl.abort();
          }
        } else if (["invalid", "failed", "error"].includes(e.event)) {
          patchState({loadingMessage: 'Error: ' + e.data});
          currentCtrl.abort();
        }
      },
      onerror: (err) => {
        if (err.name === 'AbortError') {
          return;
        }
        patchState({loadingMessage: 'Error: ' + err});
        currentCtrl.abort();
        throw err;
      }
    });
  }

  @Action(GetExtractNameFromHash)
  getExtractNameFromHash({ dispatch, getState }: StateContext<ExtractStateModel>) {
    const extractHash = getState().extractHash;
    if (!extractHash) return EMPTY;

    const request = this.http.get(`${environment.api}user/presets/query/get/${this.appConfig.dbName}/${extractHash}`);

    return dispatch(
      createRequestAction({
        request,
        state: GetExtractNameFromHashRequestState,
        successAction: GetExtractNameFromHashSuccess,
        failAction: GetExtractNameFromHashFail,
      })
    )
  }

  @Action(GetExtractNameFromHashSuccess)
  getExtractNameFromHashSuccess(
    { patchState }: StateContext<ExtractStateModel>,
    { payload: response }: GetExtractNameFromHashSuccess
  ) {
    patchState({extractName: response.content});
  }

  @Action(GetExtractNameFromHashFail)
  getExtractNameFromHashFail(
    { payload: error }: GetExtractNameFromHashFail
  ) {
    this._snackBar.open(error.error_message, 'Close', { duration: 2000 });
  }

  @Action(UpdateExtractName)
  updateExtractName(
    { patchState }: StateContext<ExtractStateModel>,
    { newName }: UpdateExtractName
  ) {
    return patchState({extractName: newName});
  }
}

@State<QueriesStateModel>({
  name: 'queries',
  defaults: {
    queries: []
  }
})
@Injectable()
export class QueriesState {
  constructor(
    private http: HttpClient,
    private store: Store,
    private localStorageService: LocalStorageService,
    private _snackBar: MatSnackBar,
    private injector: Injector,
    private appConfig: AppConfigService,
  ) {}

  @Action(GetAllQueries)
  getAllQueries(
    { dispatch }: StateContext<QueriesStateModel>,
  ) {
    const options = {params: new HttpParams().set('dbName', this.appConfig.dbName)};
    const request = this.http.get(`${environment.api}user/presets/query/list`, options);

    return dispatch(
      createRequestAction({
        request,
        state: GetAllQueriesRequestState,
        successAction: GetAllQueriesSuccess,
        failAction: GetAllQueriesFail
      })
    );
  }

  @Action(GetAllQueriesSuccess)
  getAllQueriesSuccess(
    { patchState }: StateContext<QueriesStateModel>,
    { payload: response }: GetAllQueriesSuccess
  ) {
    const queries: IQueryReduced[] = response.content.map(query => ({
      ...query,
      last_accessed_at: new Date(query.last_accessed_at) // Convert to Date
    }));
    patchState({queries: queries});
  }

  @Action(GetAllQueriesFail)
  getAllQueriesFail(
    ctx: StateContext<QueriesStateModel>,
    { payload: error }: GetAllQueriesFail
  ) {
    this._snackBar.open(error.error.error_message, 'Close', { duration: 2000 });
  }
}

@State<QueryStateModel>({
  name: 'query',
  defaults: {
    query: null,
    name: null
  }
})
@Injectable()
export class QueryState {
  constructor(
    private http: HttpClient,
    private _snackBar: MatSnackBar,
    private appConfig: AppConfigService,
  ) {}
  
  
  @Action(GetQueryDetails)
  getQueryDetails(
    { dispatch, patchState }: StateContext<QueryStateModel>,
    { name }: GetQueryDetails
  ) {
    const options = {params: new HttpParams().set('dbName', this.appConfig.dbName).set('name', name)};
    const request = this.http.get(`${environment.api}user/presets/query/get`, options);
    patchState({name: name, query: null});

    return dispatch(
      createRequestAction({
        request,
        state: GetQueryDetailsRequestState,
        successAction: GetQueryDetailsSuccess,
        failAction: GetQueryDetailsFail
      })
    );
  }

  
  @Action(GetQueryDetailsSuccess)
  getQueryDetailsSuccess(
    { patchState }: StateContext<QueryStateModel>,
    { payload: response }: GetQueryDetailsSuccess
  ) {
    patchState({query: response.content});
  }

  @Action(GetQueryDetailsFail)
  getQueryDetailsFail(
    ctx: StateContext<QueryStateModel>,
    { payload: error }: GetQueryDetailsFail
  ) {
    this._snackBar.open(error.error.error_message, 'Close', { duration: 2000 });
  }
}